import { formatDate } from '@angular/common';
import { DynamicField, DynamicForm, FieldVariant, TextFieldProps, DateFieldProps, TextareaFieldProps, SelectFieldProps, SelectOption } from '@/components/form-builder/form-builder.types';
import { ReportTaskDetailsDTO } from '@/types';
import { BehaviorSubject, Observable, of, map } from 'rxjs';
import { CustomFieldProps } from '@/components/form-builder/form-builder.types';
import { ReportModelDocComponent } from '../report-model-doc/report-model-doc.component';
import { DacDetailsComponent } from '@/pages/reports/create-reports/dac-details/dac-details.component';

export const getReportRequestDetailsFormObject = (
  data: ReportTaskDetailsDTO,
  isDraft: boolean,
  updatedDate$: BehaviorSubject<Date | null>,
  updatedMotif$: BehaviorSubject<string>,
  updatedSourceDemande$: BehaviorSubject<string>,
  dateError$: Observable<string | null>,
  motifError$: Observable<string | null>,
  sourceDemandeError$: Observable<string | null>,
  translate: (key: string) => string
): DynamicForm => {
  const isEditable = isDraft || data.isUpdatable;

  const dacRefField: DynamicField<TextFieldProps> = {
    label: 'clarification.table.dac_reference',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    props: {
      value: of(data.report.dacReference),
      onChange: () => {}
    },
  };

  const refField: DynamicField<TextFieldProps> = {
    label: 'clarification.reports.form.reference',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    props: {
      value: of(data.report.reference),
      onChange: () => {}
    },
  };

  const sourceDemandeField: DynamicField<SelectFieldProps> = {
    label: 'clarification.reports.form.source',
    variant: isEditable ? FieldVariant.SELECT : FieldVariant.TEXT,
    isDisabled: of(true),
    isRequired: isEditable,
    error: sourceDemandeError$ as any,
    props: {
      placeholder: translate('clarification.reports.form.source_placeholder'),
      options: of([
        { name: 'clarification.reports.form.source_options.DEMANDE_AC', code: 'DEMANDE_AC' },
        { name: 'clarification.reports.form.source_options.DEMANDE_CANDIDATS', code: 'DEMANDE_CANDIDATS' },
      ]),
      value: isEditable
        ? updatedSourceDemande$.pipe(map(val => val ? { name: translate(`clarification.reports.form.source_options.${val}`), code: val } : undefined))
        : of(data.report.sourceDemande ? translate(`clarification.reports.form.source_options.${data.report.sourceDemande}`) : '-'),
      onSelectChange: (option: SelectOption) => updatedSourceDemande$.next(option.code as string)
    } as any,
  };

  const dateReportField: DynamicField<DateFieldProps> = {
    label: 'clarification.reports.form.date',
    variant: isEditable ? FieldVariant.DATE : FieldVariant.TEXT,
    isDisabled: of(!isEditable),
    isRequired: isEditable,
    error: dateError$ as any,
    props: {
      showTime: true,
      value: isEditable ? updatedDate$.asObservable() : of(data.report.datereport ? formatDate(data.report.datereport, 'dd/MM/yyyy HH:mm', 'en-US', 'UTC') : '-'),
      onChange: (val: Date) => updatedDate$.next(val)
    } as any,
  };

  const motifField: DynamicField<TextareaFieldProps> = {
    label: 'clarification.reports.form.motif',
    variant: FieldVariant.TEXTAREA,
    isDisabled: of(!isEditable),
    isRequired: isEditable,
    error: motifError$ as any,
    props: {
      value: isEditable ? updatedMotif$.asObservable() : of(data.report.motifreport),
      onChange: (val: string) => updatedMotif$.next(val),
      autoResize: true,
      resize: 'allow',
      rows: 3,
    } as any,
  };

  const dacDetailsField: DynamicField<CustomFieldProps> = {
    variant: FieldVariant.CUSTOM,
    isHidden: of(!data.report.dac),
    props: {
      component: DacDetailsComponent,
      dacDetails$: of(data.report.dac),
    },
  };

  const grids: any[] = [
    {
      title: '',
      isHeaderHidden: true,
      gridItems: [
        { fields: [dacRefField, refField] },
        { fields: [dacDetailsField] },
        { fields: [sourceDemandeField] },
        { fields: [dateReportField] },
        { fields: [motifField] },
      ],
    },
  ];

  if (data.report.modelDoc) {
    grids.push({
      title: 'clarification.reports.form.model_doc',
      isHeaderHidden: false,
      gridItems: [
        {
          fields: [
            {
              variant: FieldVariant.CUSTOM,
              label: '',
              isRequired: false,
              props: {
                component: ReportModelDocComponent,
                modelDoc: data.report.modelDoc,
              } as CustomFieldProps,
            },
          ],
        },
      ],
    });
  }

  return {
    title: 'clarification.report_request.singular',
    description: '',
    isHeaderHidden: true,
    grids: grids,
  };
};
